import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Modal from '@/components/Modal/Modal';
import { useMissingVariants } from '@/services/bundles/bundles.service';
import classNames from 'classnames';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './MissingProductsModal.module.css';

export interface MissingProductsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bundleId: string;
  groupId: string;
  storeGroupName: string;
}

const MissingProductsModal: FC<MissingProductsModalProps> = ({
  isOpen,
  onClose,
  bundleId,
  groupId,
  storeGroupName,
}) => {
  const { t } = useTranslation();

  const { data, isLoading, isError } = useMissingVariants(isOpen, bundleId, groupId);

  const displayStoreGroupName = data?.data?.storeGroupName || storeGroupName;
  const missingVariants = data?.data?.missingVariants ?? [];

  return (
    <Modal
      className={styles.modal}
      maxWidth="670px"
      headerContent={
        <div className={styles.header}>
          {t('editBundle.bundleSettings.missingItemsModal.title', { storeGroupName: displayStoreGroupName })}
        </div>
      }
      showTitleDivider
      onClose={onClose}
      isOpen={isOpen}
      bodyClass={styles.modalBody}
      bodyContent={
        <div className={styles.body}>
          <p className={styles.description}>{t('editBundle.bundleSettings.missingItemsModal.description')}</p>
          {isLoading && (
            <p className={styles.loadingMessage} data-testid="missing-items-loading">
              {t('common.loading')}
            </p>
          )}
          {isError && (
            <p className={styles.errorMessage} data-testid="missing-items-error">
              {t('editBundle.bundleSettings.missingItemsModal.error')}
            </p>
          )}
          {!isLoading && !isError && (
            <div className={styles.listContainer}>
              <div className={styles.listBody}>
                <div className={styles.listHeader}>
                  <span>{t('editBundle.bundleSettings.missingItemsModal.table.missingProducts')}</span>
                  <span className={styles.storesHeader}>
                    {t('editBundle.bundleSettings.missingItemsModal.table.stores')}
                  </span>
                </div>
                {missingVariants.map((variant, index) => (
                  <div
                    key={`${variant.productName}-${variant.productDescription}`}
                    className={classNames(styles.listRow, { [styles.listRowEven]: index % 2 === 0 })}
                  >
                    <div className={styles.productCell}>
                      <div className={styles.productName}>{variant.productName}</div>
                      <div className={styles.productDescription}>{variant.productDescription}</div>
                    </div>
                    <div className={styles.storesCell}>
                      {variant.stores
                        .split(' ')
                        .filter(Boolean)
                        .map((store) => (
                          <span key={store} className={styles.storeId}>
                            {store}
                          </span>
                        ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      }
      footerContent={
        <div className={styles.footer}>
          <Button onClick={onClose} variant={ButtonVariants.Primary} data-testid="missing-items-ok">
            {t('editBundle.bundleSettings.missingItemsModal.ok')}
          </Button>
        </div>
      }
    />
  );
};

export default MissingProductsModal;
